import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import {
  Send,
  Plus,
  BellRing,
  Activity,
  CheckCircle2,
  XCircle,
  Clock,
  Zap,
  BarChart3,
  TrendingUp,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import AdminLayout from "@/components/layout/AdminLayout";

import { supabase } from "@/integrations/supabase/client";

interface AnalyticsSummary {
  total_notifications: number | null;
  total_delivered: number | null;
  total_opened: number | null;
  total_clicked: number | null;
  total_failed: number | null;
  total_pending: number | null;
  delivery_rate: number | null;
  open_rate: number | null;
  click_rate: number | null;
}

interface SystemHealth {
  notifications_last_hour: number | null;
  attempts_last_hour: number | null;
  pending_attempts: number | null;
  retry_queue: number | null;
  delivery_success_rate_24h: number | null;
}

interface RecentDelivery {
  delivery_id: number | null;
  rendered_title: string | null;
  template_name: string | null;
  overall_status: string | null;
  created_at: string | null;
  total_devices: number | null;
  successful_devices: number | null;
}

interface UpcomingCampaign {
  id: number;
  title: string;
  status: string;
  scheduled_at: string | null;
  target_count: number | null;
}

export default function NotificationDashboard() {
  const navigate = useNavigate();
  const [summary, setSummary] = useState<AnalyticsSummary | null>(null);
  const [health, setHealth] = useState<SystemHealth | null>(null);
  const [recentDeliveries, setRecentDeliveries] = useState<RecentDelivery[]>([]);
  const [upcomingCampaigns, setUpcomingCampaigns] = useState<UpcomingCampaign[]>([]);
  const [automationCount, setAutomationCount] = useState(0);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchDashboardData();
  }, []);

  const fetchDashboardData = async () => {
    try {
      setLoading(true);

      // Fetch all data in parallel
      const [summaryRes, healthRes, deliveriesRes, campaignsRes, automationRes] =
        await Promise.all([
          supabase
            .from("notification_analytics_summary")
            .select("*")
            .single(),
          supabase
            .from("notification_system_health")
            .select("*")
            .single(),
          supabase
            .from("notification_delivery_stats")
            .select(
              "delivery_id, rendered_title, template_name, overall_status, created_at, total_devices, successful_devices"
            )
            .order("created_at", { ascending: false })
            .limit(10),
          supabase
            .from("group_campaigns")
            .select("id, title, status, scheduled_at, target_count")
            .in("status", ["scheduled", "sending"])
            .order("scheduled_at", { ascending: true })
            .limit(5),
          supabase
            .from("notification_automation_rules")
            .select("id", { count: "exact", head: true })
            .eq("is_active", true),
        ]);

      if (summaryRes.data) setSummary(summaryRes.data);
      if (healthRes.data) setHealth(healthRes.data);
      if (deliveriesRes.data) setRecentDeliveries(deliveriesRes.data as RecentDelivery[]);
      if (campaignsRes.data) setUpcomingCampaigns(campaignsRes.data);
      setAutomationCount(automationRes.count || 0);
    } catch (error) {
      console.error("Error fetching dashboard data:", error);
    } finally {
      setLoading(false);
    }
  };

  const getStatusBadge = (status: string | null) => {
    switch (status) {
      case "delivered":
      case "sent":
        return <Badge className="bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-300">Delivered</Badge>;
      case "opened":
      case "clicked":
        return <Badge className="bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-300">{status}</Badge>;
      case "failed":
        return <Badge variant="destructive">Failed</Badge>;
      case "pending":
        return <Badge variant="secondary">Pending</Badge>;
      default:
        return <Badge variant="outline">{status || "Unknown"}</Badge>;
    }
  };

  return (
    <AdminLayout title="Notification Dashboard">
      <div className="space-y-6">
        {/* Quick Actions */}
        <div className="flex items-center gap-3">
          <Button onClick={() => navigate("/admin/notifications/send")}>
            <Send className="h-4 w-4 mr-2" />
            Send Notification
          </Button>
          <Button
            variant="outline"
            onClick={() => navigate("/admin/notifications/templates/new")}
          >
            <Plus className="h-4 w-4 mr-2" />
            Create Template
          </Button>
          <Button
            variant="outline"
            onClick={() => navigate("/admin/notifications/campaigns/new")}
          >
            <BellRing className="h-4 w-4 mr-2" />
            Create Campaign
          </Button>
        </div>

        {/* Stats Cards */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <Card>
            <CardContent className="pt-6">
              <div className="text-2xl font-bold">
                {summary?.total_notifications ?? "-"}
              </div>
              <p className="text-xs text-muted-foreground">Total Notifications</p>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="pt-6">
              <div className="flex items-center gap-2">
                <div className="text-2xl font-bold text-green-600">
                  {summary?.delivery_rate ?? "-"}%
                </div>
                <CheckCircle2 className="h-5 w-5 text-green-600" />
              </div>
              <p className="text-xs text-muted-foreground">Delivery Rate</p>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="pt-6">
              <div className="flex items-center gap-2">
                <div className="text-2xl font-bold text-blue-600">
                  {summary?.open_rate ?? "-"}%
                </div>
                <TrendingUp className="h-5 w-5 text-blue-600" />
              </div>
              <p className="text-xs text-muted-foreground">Open Rate</p>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="pt-6">
              <div className="flex items-center gap-2">
                <div className="text-2xl font-bold text-red-600">
                  {summary?.total_failed ?? "-"}
                </div>
                <XCircle className="h-5 w-5 text-red-600" />
              </div>
              <p className="text-xs text-muted-foreground">Failed</p>
            </CardContent>
          </Card>
        </div>

        {/* System Health + Automation */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <Card>
            <CardHeader className="pb-3">
              <CardTitle className="text-sm font-medium flex items-center gap-2">
                <Activity className="h-4 w-4" />
                System Health (24h)
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-2">
              <div className="flex items-center justify-between text-sm">
                <span className="text-muted-foreground">Notifications/hr</span>
                <span className="font-medium">{health?.notifications_last_hour ?? "-"}</span>
              </div>
              <div className="flex items-center justify-between text-sm">
                <span className="text-muted-foreground">Success Rate</span>
                <span className="font-medium">{health?.delivery_success_rate_24h ?? "-"}%</span>
              </div>
              <div className="flex items-center justify-between text-sm">
                <span className="text-muted-foreground">Pending</span>
                <span className="font-medium">{health?.pending_attempts ?? "-"}</span>
              </div>
              <div className="flex items-center justify-between text-sm">
                <span className="text-muted-foreground">Retry Queue</span>
                <span className="font-medium">{health?.retry_queue ?? "-"}</span>
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardHeader className="pb-3">
              <CardTitle className="text-sm font-medium flex items-center gap-2">
                <Zap className="h-4 w-4" />
                Active Automation Rules
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="text-3xl font-bold">{automationCount}</div>
              <Button
                variant="link"
                className="p-0 h-auto text-sm"
                onClick={() => navigate("/admin/notifications/automation")}
              >
                Manage Rules
              </Button>
            </CardContent>
          </Card>

          <Card>
            <CardHeader className="pb-3">
              <CardTitle className="text-sm font-medium flex items-center gap-2">
                <Clock className="h-4 w-4" />
                Upcoming Campaigns
              </CardTitle>
            </CardHeader>
            <CardContent>
              {upcomingCampaigns.length === 0 ? (
                <p className="text-sm text-muted-foreground">No upcoming campaigns</p>
              ) : (
                <div className="space-y-2">
                  {upcomingCampaigns.map((campaign) => (
                    <div
                      key={campaign.id}
                      className="flex items-center justify-between text-sm"
                    >
                      <span className="truncate max-w-[150px]">{campaign.title}</span>
                      <Badge variant="outline" className="text-xs">
                        {campaign.scheduled_at
                          ? new Date(campaign.scheduled_at).toLocaleDateString()
                          : "Now"}
                      </Badge>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        </div>

        {/* Recent Deliveries */}
        <Card>
          <CardHeader className="flex flex-row items-center justify-between">
            <CardTitle className="flex items-center gap-2">
              <BarChart3 className="h-5 w-5" />
              Recent Deliveries
            </CardTitle>
            <Button
              variant="outline"
              size="sm"
              onClick={() => navigate("/admin/notifications/analytics")}
            >
              View All
            </Button>
          </CardHeader>
          <CardContent>
            {recentDeliveries.length === 0 ? (
              <p className="text-sm text-muted-foreground text-center py-8">
                No deliveries yet. Send your first notification!
              </p>
            ) : (
              <div className="space-y-3">
                {recentDeliveries.map((delivery, index) => (
                  <div
                    key={delivery.delivery_id ?? index}
                    className="flex items-center justify-between p-3 rounded-md border"
                  >
                    <div className="min-w-0 flex-1">
                      <div className="font-medium text-sm truncate">
                        {delivery.rendered_title || "Untitled"}
                      </div>
                      <div className="text-xs text-muted-foreground">
                        {delivery.template_name || "No template"} &middot;{" "}
                        {delivery.created_at
                          ? new Date(delivery.created_at).toLocaleString()
                          : "Unknown"}
                      </div>
                    </div>
                    <div className="flex items-center gap-2 ml-4">
                      <span className="text-xs text-muted-foreground">
                        {delivery.successful_devices ?? 0}/{delivery.total_devices ?? 0} devices
                      </span>
                      {getStatusBadge(delivery.overall_status)}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </AdminLayout>
  );
}
